"use client";

import React from "react";
import { v4 as uuidv4 } from "uuid";
import {
  RT_MAIN_CTA,
  RT_FOOTER_BG,
  RT_FOOTER_BG_MOBILE,
  RT_FOOTER_NEW,
  RT_FOOTER_BANNER_MOBILE,
  RT_LOGO_URL,
  RT_FOOTER_BANNER,
} from "@/data";
import { footerData } from "@/data/footerData";
import { MemoizedStars } from "@/components/ui/stars";
import Logo from "@/components/ratl/utility/logo-nav";
import { useRouter } from "next/navigation";
import { Badge } from "@/components/sections/PricingBody";
import CustomButton from "@/components/ratl/utility/CustonButton";
import { useFloat } from "@/lib";
import { FlipWords } from "@/components/ui/flip-words";
import Picture from "@/components/ratl/utility/Picture";
import OnlineStatusIndicator from "@/components/ui/status-indicator";
import BookADemo from "@/components/ui/book-a-demo";
import Image from "next/image";
import PoweredByModels from "./PoweredByModels";
import MobileExploreLinks from "./MobileExploreLinks";
import FooterLinks from "./FooterLinks";
import SocialLinks from "./SocialLinks";
import Link from "next/link";

const Footer: React.FC = () => {
  const { items, socials, ratlfooterlinks } = footerData;
  const router = useRouter();
  const { float, handleFloat } = useFloat([false, false]);
  const words = ["Mumbai", "Bengaluru", "Dubai", "San Jose"];
  const footerColumns = [
    {
      title: "Product",
      links: [
        { label: "Self-serve", href: "/self-serve" },
        { label: "Managed QA", href: "/managed" },
        { label: "Pricing", href: "/pricing" },
        { label: "Enterprise", href: "/enterprise-solutions" },
        
      ],
    },
    {
      title: "Solutions",
      links: [
        // { label: "Use cases", href: "/use-cases" },
        { label: "Customers", href: "/customers" },
        { label: "Commerce QA", href: "/customers/commerce" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Blog", href: "/blog" },
        { label: "Docs", href: "/docs" },
        { label: "Newsletter", href: "/newsletter" },
        // { label: "Book a demo", href: "/book-demo" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About us", href: "/about-us" },
        { label: "Contact", href: "/contact-us" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Terms of use", href: "/terms-of-use" },
        { label: "Privacy policy", href: "/privacy-policy" },
      ],
    },
  ];

  return (
    <footer
      className="w-full bg-background-2 flex flex-col mt-20 md:mt-20"
      aria-label="Site Footer"
      role="contentinfo"
    >
      {/* Top Section: Call-to-action */}
      <section
        className={`relative flex flex-col items-center justify-center text-center m-4 p-10 md:m-0 md:p-0 px-4 gap-8  md:py-12 md:px-8 overflow-hidden rounded-[20px] w-fit md:w-3/4 md:mx-auto`}
        aria-labelledby="footer-cta-heading"
        style={{
          backgroundImage: `url(${RT_FOOTER_BANNER_MOBILE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          ...(typeof window !== 'undefined' && window.innerWidth >= 768
            ? { backgroundImage: `url(${RT_FOOTER_BANNER})` }
            : {}),
        }}
      >
        <div className="absolute inset-0 bg-black/20 rounded-[20px] pointer-events-none z-0" />
        <div className="flex flex-col gap-4 justify-center items-center z-20 w-full max-w-2xl mx-auto relative">
          <h1 id="footer-cta-heading" className="text-2xl sm:text-3xl md:text-4xl leading-[130%] font-semibold w-full text-center">
            AI-powered platform to <br />
            <span className="text-2xl sm:text-3xl md:text-4xl font-normal md:font-semibold">
              revolutionize testing and reliability
            </span>
          </h1>
          <h2 className="text-base sm:text-lg md:text-xl text-light-main-heading max-w-2xl leading-[130%] font-inter w-full text-center">
            Smarter testing, <br className="md:hidden" /> stronger products.
            ratl.ai empowers <br className="md:hidden" /> teams with AI-driven <br className="md:block hidden" /> reliability and <br className="md:hidden" /> seamless innovation.
          </h2>
        </div>
        <div className="flex flex-col gap-2 justify-center items-center z-20 relative">
          <BookADemo />
          <a
            href="/newsletter"
            className="hover:underline font-normal hover:font-semibold"
            tabIndex={0}
            aria-label="Newsletter"
            role="link"
          >
            Join the Newsletter
          </a>
        </div>
      </section>
      {/* Middle Section: Content with background images and stars */}
      <section className="flex flex-col items-center bg-background-2 text-white md:pt-16 md:pb-6 w-full" aria-labelledby="footer-main-content">
        <div className="flex flex-col items-center px-4 py-6 pt-16 md:pt-0 w-full max-w-2xl text-center">
          <Logo logoSrc={RT_LOGO_URL} width={140} />
          <p className="text-base opacity-80 my-4 font-inter text-rt-grey-t1">
            Empower your testing with precision and speed. Welcome to the future
            of automated testing with ratl.ai, where efficiency meets
            intelligence.
          </p>
          <PoweredByModels />
        </div>
        <div className="w-full px-4 md:px-8 font-inter py-8 md:py-0">
          <div className="mt-8 grid w-full max-w-6xl mx-auto grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 text-left">
            {footerColumns.map((column) => (
              <div key={column.title} className="flex flex-col gap-3">
                <p className="text-sm font-semibold text-white/90">
                  {column.title}
                </p>
                <div className="flex flex-col gap-2">
                  {column.links.map((link) => {
                    const isDocsLink = link.href === "/docs";
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        target={isDocsLink ? "_blank" : undefined}
                        rel={isDocsLink ? "noreferrer" : undefined}
                        className="text-sm text-white/60 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-background-2 w-full px-4 md:px-8 text-sm flex flex-col md:flex-col justify-center md:justify-between items-center pb-4">
          <div className="w-full" aria-hidden="true">
            <Image
              src={RT_FOOTER_NEW}
              alt="Footer Background"
              width={900}
              height={160}
              className="transition ease-in duration-200 hidden md:block w-full h-auto"
            />
          </div>
          <div className="flex flex-col-reverse md:flex-row border-t border-light-sub-heading/30 justify-center md:justify-between items-center w-full gap-4">
            <FooterLinks words={words} />
            <SocialLinks socials={socials} />
          </div>
        </div>
      </section>
    </footer>
  );
};

export default Footer;
